import React, {
  createContext, useContext, useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { subscribeChats } from '../firebase/chatService';

// ─── NOTIFICATION SYSTEM ─────────────────────────────────────────────────────
//
// One place that answers "a message arrived" for the whole app: the unread
// badge, the sound, the in-app toast, and the browser notification.
//
// WHERE THE EVENTS COME FROM. No webhook or socket is needed — subscribeChats
// is already a live Firestore listener on every conversation this user is in,
// and Firestore pushes each change down the open connection. Adding a second
// transport for the same events would mean two sources that can disagree about
// what is unread.
//
// ONE LISTENER, NOT ONE PER COMPONENT. It lives here, at the top of the app, so
// the envelope in the header, the toasts and the sound all read from the same
// snapshot. The header badge in particular used to be a cached one-shot fetch
// that never went down when you read a message; now it is derived from the same
// live data, so it drops the moment markChatRead lands.
//
// WHAT CANNOT BE TURNED OFF. Sound, badge and toast are always on — a message
// you never notice is the failure this exists to prevent. Only the BROWSER
// notification is optional, because that one escapes the tab and lands on the
// user's desktop.

const NotificationCtx = createContext(null);

export const useNotificationSystem = () => useContext(NotificationCtx) || {};

const PUSH_PREF_KEY = 'tunify.browserPush';
// A toast is a nudge, not a document; long enough to read a name and a line.
const TOAST_MS = 5000;
const SOUND_URL = '/media/new-message.wav';
// Two messages a second apart should not play the sound twice — that reads as a
// glitch rather than as two messages.
const SOUND_THROTTLE_MS = 1500;

const readPushPref = () => {
  try { return localStorage.getItem(PUSH_PREF_KEY) === '1'; } catch { return false; }
};

export function NotificationProvider({ children }) {
  const { user } = useAuth();

  const [unreadTotal, setUnreadTotal] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [pushEnabled, setPushEnabled] = useState(readPushPref);
  const [permission, setPermission] = useState(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );

  // Previous per-chat unread counts. The comparison against this is what turns
  // a stream of snapshots into "a new message arrived" — Firestore re-sends the
  // whole set on any change, including ones we caused ourselves.
  const prevUnread = useRef(null);
  const audioRef = useRef(null);
  const lastSoundAt = useRef(0);
  // Whether the server can push to this device — see showBrowserNotification.
  const hasPushSub = useRef(false);

  // ── sound ─────────────────────────────────────────────────────────────────
  const playSound = useCallback(() => {
    const now = Date.now();
    if (now - lastSoundAt.current < SOUND_THROTTLE_MS) return;
    lastSoundAt.current = now;
    try {
      // Reused rather than constructed per message: a new Audio each time leaks
      // elements, and rewinding lets a second message interrupt the first.
      if (!audioRef.current) {
        audioRef.current = new Audio(SOUND_URL);
        audioRef.current.volume = 0.35;
      }
      audioRef.current.currentTime = 0;
      // Autoplay is blocked until the user has interacted with the page. That
      // rejection is expected and harmless — the toast and badge still land.
      audioRef.current.play().catch(() => {});
    } catch { /* no audio available; the visible parts still work */ }
  }, []);

  // ── toasts ────────────────────────────────────────────────────────────────
  const dismissToast = useCallback((id) => {
    setToasts(list => list.filter(x => x.id !== id));
  }, []);

  const pushToast = useCallback((toast) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts(list => [...list.slice(-2), { ...toast, id }]);   // at most three
    setTimeout(() => dismissToast(id), TOAST_MS);
  }, [dismissToast]);

  // ── browser notification ──────────────────────────────────────────────────
  const enablePush = useCallback(async () => {
    if (typeof Notification === 'undefined') return false;
    let perm = Notification.permission;
    if (perm === 'default') perm = await Notification.requestPermission();
    setPermission(perm);
    const on = perm === 'granted';
    setPushEnabled(on);
    try { localStorage.setItem(PUSH_PREF_KEY, on ? '1' : '0'); } catch { /* ignore */ }
    return on;
  }, []);

  const disablePush = useCallback(() => {
    setPushEnabled(false);
    try { localStorage.setItem(PUSH_PREF_KEY, '0'); } catch { /* ignore */ }
  }, []);

  const setBrowserPush = useCallback((on) => (on ? enablePush() : (disablePush(), false)),
    [enablePush, disablePush]);

  const showBrowserNotification = useCallback((title, body) => {
    // The ONLY part the preference gates. Everything else has already happened
    // by the time this runs.
    if (!pushEnabled) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    // NOT WHEN THE SERVER IS ALREADY PUSHING TO THIS DEVICE.
    //
    // Two independent things notice a new message: this Firestore listener, and
    // the onMessageCreated Cloud Function. Once the device has a push
    // subscription the function's notification is drawn by the browser itself,
    // so drawing another one here showed every message twice.
    //
    // A live push subscription is the ground truth for "the server can reach
    // this device", which is exactly the question — better than a stored flag,
    // which goes stale the moment a token rotates.
    if (hasPushSub.current) return;

    try {
      const n = new Notification(title, {
        body,
        icon: '/images/Tunify Original logo tab.png',
        // Same tag for every message so a burst replaces itself rather than
        // stacking ten notifications the user has to dismiss one by one.
        tag: 'tunify-message',
        renotify: true,
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch { /* some browsers throw on construction in insecure contexts */ }
  }, [pushEnabled]);

  // Re-checked whenever the preference changes, so turning push on or off
  // takes effect without a reload.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!('serviceWorker' in navigator)) return;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (alive) hasPushSub.current = !!sub;
      } catch {
        // No subscription discoverable — fall back to notifying from the page,
        // which is the safe direction to fail in: one notification, not none.
        if (alive) hasPushSub.current = false;
      }
    })();
    return () => { alive = false; };
  }, [pushEnabled]);

  // ── the one entry point for "a message arrived" ───────────────────────────
  const notifyMessage = useCallback(({ from, text, chatId, avatar }) => {
    playSound();
    pushToast({ from, text, chatId, avatar });
    showBrowserNotification(from, text);
  }, [playSound, pushToast, showBrowserNotification]);

  // ── the live listener ─────────────────────────────────────────────────────
  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      setUnreadTotal(0);
      setToasts([]);
      prevUnread.current = null;
      return undefined;
    }

    return subscribeChats(uid, (chats) => {
      const total = chats.reduce((sum, c) => sum + (c.unread?.[uid] || 0), 0);
      setUnreadTotal(total);

      const next = new Map(chats.map(c => [c.id, c.unread?.[uid] || 0]));
      const prev = prevUnread.current;
      prevUnread.current = next;

      // The FIRST snapshot is the existing state of the inbox, not news. Without
      // this, opening the app with four unread messages would fire four toasts
      // and four sounds for messages that arrived yesterday.
      if (!prev) return;

      for (const chat of chats) {
        const before = prev.get(chat.id) ?? 0;
        const now = chat.unread?.[uid] || 0;
        if (now <= before) continue;                      // read, or unchanged
        if (chat.lastSenderUid === uid) continue;         // my own message
        notifyMessage({
          chatId: chat.id,
          from: chat.participantInfo?.[chat.lastSenderUid]?.name || 'New message',
          avatar: chat.participantInfo?.[chat.lastSenderUid]?.avatar || '',
          text: chat.lastMessage || '',
        });
      }
    });
  }, [user?.uid, notifyMessage]);

  const value = useMemo(() => ({
    unreadTotal,
    toasts,
    dismissToast,
    // Settings
    pushEnabled,
    permission,
    setBrowserPush,
    // Exposed so anything else that learns of a message (a future webhook, a
    // manual test) can raise one through the same path.
    notifyMessage,
  }), [unreadTotal, toasts, dismissToast, pushEnabled, permission, setBrowserPush, notifyMessage]);

  return <NotificationCtx.Provider value={value}>{children}</NotificationCtx.Provider>;
}
